import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Paper } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "notistack";
import { NoRecordFound } from "../shared_elements";
import AiChatBrand from "./AiChatBrand";
import { MockApi } from "./api/mockApi";
import TokenAdminAccess from "../hoc/TokenAdminAccess";
import { trackActivity } from "../utils/mixpanel";
import { useAiConsent } from "./AiConsentGate";
import { isForbiddenError } from "./consentConstants";
import "./tokenManagement.css";

function formatDate(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "2-digit",
    year: "numeric"
  }).format(parsed);
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

export function TokenUsage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { accepted: consentAccepted, requestReconsent } = useAiConsent();

  const [skeletonLoader, setSkeletonLoader] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [usageList, setUsageList] = useState([]);

  async function loadUsageList(loaderType = "pageLoader") {
    if (loaderType === "skeletonLoader") setSkeletonLoader(true);
    else setIsLoading(true);

    try {
      const response = await MockApi.loadLessorTokenUsage();
      setUsageList(Array.isArray(response?.list) ? response.list : []);
    } catch (error) {
      setUsageList([]);
      if (!(isForbiddenError(error) && (await requestReconsent(error)))) {
        enqueueSnackbar(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load token usage.",
          { variant: "error" }
        );
      }
    } finally {
      setSkeletonLoader(false);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!consentAccepted) {
      setSkeletonLoader(false);
      return;
    }
    loadUsageList("skeletonLoader");
    trackActivity("Page Viewed", { page_title: "Token Usage" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentAccepted]);

  const handleItemClicked = (page_type) => {
    trackActivity("Item Clicked", {
      page_title: "Token Usage",
      page_type
    });
  };

  return (
    <section className="active-asset-section token-management-page">
      <div className="token-page-header">
        <AiChatBrand />
        <button
          type="button"
          className="token-back-link"
          onClick={() => {
            handleItemClicked("Back to AI Chat");
            navigate("/ai-chat");
          }}
        >
          <ArrowBackIcon fontSize="inherit" />
          Back to AI Chat
        </button>
      </div>

      <Paper className="token-request-panel" square>
        <div className="request-history">
          <div className="requests-header-row">
            <h3>Token Usage</h3>
          </div>

          <div className="request-list-scroll token-usage-scroll">
            {skeletonLoader || isLoading ? (
              <div className="modal-loading">Loading token usage…</div>
            ) : usageList.length ? (
              <div className="lessor-usage-list">
                {usageList.map((lessor, index) => (
                  <section
                    className="lessor-usage-section"
                    key={`${lessor.lessor_id}-${lessor.lessor_slug}-${index}`}
                  >
                    <div className="lessor-usage-summary">
                      <div className="lessor-usage-summary-main">
                        <h4>{lessor.lessor_name || "Unknown lessor"}</h4>
                      </div>
                      <div className="lessor-usage-stats">
                        <div className="lessor-usage-stat">
                          <span className="lessor-usage-stat-label">
                            Tokens Consumed
                          </span>
                          <strong className="tokens-consumed-value">
                            {formatNumber(lessor.overall_tokens_consumed)}
                          </strong>
                        </div>
                        <div className="lessor-usage-stat">
                          <span className="lessor-usage-stat-label">
                            Available tokens
                          </span>
                          <strong>
                            {formatNumber(lessor.available_tokens)}
                          </strong>
                        </div>
                        <div className="lessor-usage-stat">
                          <span className="lessor-usage-stat-label">
                            Last recharged
                          </span>
                          <strong>{formatDate(lessor.last_recharged)}</strong>
                        </div>
                        <div className="lessor-usage-stat">
                          <span className="lessor-usage-stat-label">No. of Users</span>
                          <strong>
                            {formatNumber(lessor.users?.length || 0)}
                          </strong>
                        </div>
                      </div>
                    </div>

                    {lessor.users?.length ? (
                      <div className="lessor-usage-body">
                        <div className="request-list user-usage-list">
                          {lessor.users.map((user, userIndex) => (
                            <div
                              className="request-item"
                              key={`${lessor.lessor_id}-${user.id || user.email || user.name}-${userIndex}`}
                            >
                              <div className="request-item-summary">
                                <div className="request-item-main">
                                  <div className="request-token-count">
                                    <strong>{user.name || "Unknown user"}</strong>
                                  </div>
                                  <div className="request-details user-usage-details">
                                    <div className="request-details-primary">
                                      <span>{user.email || "No email"}</span>
                                      <span>
                                        Questions:{" "}
                                        {formatNumber(user.no_of_questions)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="request-status-actions">
                                  <span className="tokens-consumed-badge">
                                    {formatNumber(user.tokens_consumed)} consumed
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="lessor-usage-body">
                        <NoRecordFound />
                      </div>
                    )}
                  </section>
                ))}
              </div>
            ) : (
              <NoRecordFound />
            )}
          </div>
        </div>
      </Paper>
    </section>
  );
}

export default TokenAdminAccess(TokenUsage);
